import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Loader2, Save, Link2 } from "lucide-react";
import { http, errMsg } from "@/lib/data";

const RefRow = ({ x, headers }) => {
  const [url, setUrl] = useState(x.referral_url);
  const [busy, setBusy] = useState(false);
  const qc = useQueryClient();
  const dirty = url.trim() !== x.referral_url;
  const save = async () => {
    setBusy(true);
    try {
      await http.put(`/admin/referrals/${x.id}`, { url: url.trim() }, { headers });
      toast.success(url.trim() ? `Referral link saved for ${x.name}` : `${x.name} now uses its official website`);
      qc.invalidateQueries({ queryKey: ["admin-referrals"] });
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const c = x.clicks;
  return (
    <tr data-testid={`ref-row-${x.id}`} className="border-t border-slate-100 align-top">
      <td className="px-4 py-3 min-w-[180px]"><p className="font-semibold">{x.name}</p><p className="text-xs text-slate-500">{x.kind}</p></td>
      <td className="px-4 py-3 min-w-[320px]">
        <input data-testid={`ref-input-${x.id}`} value={url} onChange={(e) => setUrl(e.target.value)} placeholder={x.website || "https://"} className="w-full h-10 rounded-lg border border-slate-200 px-3 text-sm outline-none focus:ring-2 focus:ring-[#10B981]" />
        <p data-testid={`ref-status-${x.id}`} className={`text-[11px] mt-1 ${x.referral_url ? "text-[#059669] font-bold" : "text-slate-400"}`}>{x.referral_url ? "Referral link active" : "Using official website"}</p>
      </td>
      <td className="px-4 py-3"><button data-testid={`ref-save-${x.id}`} disabled={!dirty || busy} onClick={save} className="h-10 px-3 rounded-lg bg-[#10B981] hover:bg-[#059669] disabled:opacity-40 text-white text-xs font-bold inline-flex items-center gap-1.5">{busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}Save</button></td>
      <td data-testid={`ref-clicks-${x.id}`} className="px-4 py-3 text-right font-mono whitespace-nowrap"><p className="font-bold">{c.total}</p><p className="text-[11px] text-slate-500">S {c.search || 0} · C {c.compare || 0} · P {c.card || 0}{c.other ? ` · O ${c.other}` : ""}</p></td>
    </tr>
  );
};

export const ReferralsAdmin = ({ token, onExpired }) => {
  const headers = { Authorization: `Bearer ${token}` };
  const [f, setF] = useState("");
  const { data = [], isLoading, error } = useQuery({ queryKey: ["admin-referrals", token], queryFn: () => http.get("/admin/referrals", { headers }).then((r) => r.data), retry: false });
  useEffect(() => { if (error?.response?.status === 401) onExpired(); }, [error, onExpired]);
  const rows = data.filter((x) => `${x.name} ${x.kind}`.toLowerCase().includes(f.toLowerCase()));
  const active = data.filter((x) => x.referral_url).length;
  return (
    <div className="bg-white rounded-2xl border border-slate-200 mt-4">
      <div className="flex flex-wrap items-center justify-between gap-3 p-4 border-b border-slate-100">
        <p data-testid="ref-summary" className="font-semibold inline-flex items-center gap-2"><Link2 className="w-4 h-4 text-[#059669]" />{active} of {data.length} providers have a referral link</p>
        <input data-testid="ref-filter" value={f} onChange={(e) => setF(e.target.value)} placeholder="Filter by name or type…" className="h-10 w-64 rounded-lg border border-slate-200 px-3 text-sm outline-none focus:ring-2 focus:ring-[#10B981]" />
      </div>
      <p className="px-4 pt-3 text-xs text-slate-500">Paste your affiliate link and save. Every "Visit" click on the site is tracked (S = search, C = compare, P = provider card, O = other) and redirected to your link, or to the official website when empty.</p>
      <div className="overflow-x-auto">
        {isLoading ? <p className="p-6 text-sm text-slate-500">Loading…</p> : (
          <table data-testid="admin-referrals-table" className="w-full text-sm mt-2">
            <thead><tr className="text-left text-xs uppercase tracking-wide text-slate-500"><th className="px-4 py-2">Provider</th><th className="px-4 py-2">Referral / affiliate link</th><th className="px-4 py-2" /><th className="px-4 py-2 text-right">Clicks</th></tr></thead>
            <tbody>{rows.map((x) => <RefRow key={`${x.id}-${x.referral_url}`} x={x} headers={headers} />)}</tbody>
          </table>
        )}
      </div>
    </div>
  );
};
